// Friendly copy for stable API error CODES.
//
// Some 4xx responses return a stable, snake_case machine code in the JSON
// body's `error` field (e.g. "item_already_on_presentation") instead of a
// raw exception message — raw messages leaked internal UUIDs + index names
// to the client. extractError consults this map before falling back to the
// raw body, so the client sees a human sentence.
//
// Keep this map TINY and local — it is deliberately NOT an i18n layer. Add
// a code here only when a route starts returning it.
export const API_ERROR_COPY: Readonly<Record<string, string>> = {
  item_already_on_presentation: "This item is already on the presentation.",
  item_not_in_presentation:
    "One of your notes is attached to a product that's no longer in this presentation. Please refresh and try again.",
  color_not_found:
    "That color isn't available on this product anymore. Refresh and try again.",
  supplier_image_fetch_failed:
    "Couldn't fetch that color's photo from the supplier. Try again in a minute.",
  mirror_unavailable:
    "Image storage isn't configured in this environment, so color photos can't be copied yet.",
  // Category codes (2026-08-06/07). Without these, extractError falls through to
  // the raw body and a rep sees the literal snake_case code — exactly what this
  // map exists to prevent. Both are reachable on a concurrent edit: another tab,
  // or the rep's own second window, removes or moves a category while this one is
  // filing a product into it.
  // ⚠️ These keys are scoped by CURRENT USAGE, not by name. `item_not_found` is
  // generic-sounding and this map is shared app-wide; today only the section
  // routes emit it, so the wording is safe. If another route ever returns the
  // same code for an unrelated resource, a rep would be told a product left the
  // presentation when the real problem was something else. Check before reusing.
  section_mismatch:
    "That category belongs to a different presentation. Refresh and try again.",
  item_not_found:
    "That product isn't on this presentation anymore. Refresh and try again.",
  // Not snake_case, because the section CRUD routes return this phrase in the
  // `error` field rather than a code. Mapped by its literal text so it cannot
  // reach a rep as-is; if those routes ever adopt a code, add it alongside.
  "section not found":
    "That category no longer exists — someone may have removed it. Refresh and try again.",
};

/** Friendly copy for a known API error code, or null when the code is
 *  unmapped/absent (caller falls through to its raw-body fallback). */
export function friendlyApiError(code: string | null | undefined): string | null {
  if (!code) return null;
  return API_ERROR_COPY[code] ?? null;
}
